import { Fragment, useState } from "react";
import { 
  Dialog, 
  DialogPanel, 
  DialogTitle,
  Transition, 
  TransitionChild,
  Menu,
} from "@headlessui/react";
import { 
  XMarkIcon,
  PlusIcon,
  FunnelIcon,
  EllipsisHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "@heroicons/react/24/outline";
import { 
  Button, 
  Badge,
} from "components/ui";
import clsx from "clsx";
import { AddTaxRateModal } from "./AddTaxRateModal";
import { TaxesFilterDrawer } from "./TaxesFilterDrawer";

const MOCK_TAXES = [
    { id: 1, name: "gstedic", agency: "Unassigned", percentage: "53.00%", lastUpdated: "Apr 10, 2026", status: "Active", updatedBy: "Vishal Panjia", group: "--" }
];

export function TaxesModal({ show, onClose }) {
  const [activeTab, setActiveTab] = useState("Tax rates");
  const [showAddTax, setShowAddTax] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  return (
    <>
      <Transition show={show} as={Fragment}>
        <Dialog as="div" className="relative z-[410]" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity dark:bg-black/60" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-hidden text-left">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative flex h-[70vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all dark:bg-dark-750 border border-slate-100 dark:border-dark-600 isolate">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-dark-600 bg-slate-50/50">
                    <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                      Taxes
                    </DialogTitle>
                    <button
                      onClick={onClose}
                      className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-500 dark:text-dark-300 dark:hover:bg-dark-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between px-6 py-3 border-b border-slate-50 dark:border-dark-600">
                    <div className="flex gap-1 p-1 bg-slate-100 dark:bg-dark-800 rounded-xl">
                        {["Tax rates", "Tax groups"].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={clsx(
                                    "px-4 py-1.5 text-xs font-bold rounded-lg transition-all",
                                    activeTab === tab ? "bg-white dark:bg-dark-700 text-primary shadow-sm" : "text-slate-500 hover:text-slate-700 dark:text-dark-300"
                                )}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Menu as="div" className="relative">
                            <Menu.Button as={Fragment}>
                                <Button variant="flat" isIcon size="sm" className="text-slate-400 h-9 w-9">
                                    <EllipsisHorizontalIcon className="h-5 w-5" />
                                </Button>
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-dark-800 shadow-premium border border-slate-100 dark:border-dark-600 p-1.5 focus:outline-none z-100">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button className={clsx("w-full text-left px-3 py-2 text-xs font-bold rounded-lg transition-colors", active ? "bg-slate-50 dark:bg-dark-700 text-slate-900 dark:text-white" : "text-slate-600 dark:text-dark-200")}>
                                                Export
                                            </button>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button className={clsx("w-full text-left px-3 py-2 text-xs font-bold rounded-lg text-rose-500 transition-colors", active ? "bg-rose-50 dark:bg-rose-500/10" : "")}>
                                                Disable tax features
                                            </button>
                                        )}
                                    </Menu.Item>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                        <Button variant="flat" isIcon size="sm" className="text-slate-400 h-9 w-9" onClick={() => setShowFilter(true)}>
                            <FunnelIcon className="h-5 w-5" />
                        </Button>
                        <Button variant="filled" color="primary" size="sm" className="font-bold text-xs h-9 px-4" onClick={() => setShowAddTax(true)}>
                            <PlusIcon className="h-4 w-4 mr-1.5 shadow-none" /> Tax rate
                        </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex-1 overflow-auto no-scrollbar scrollbar-sm">
                        <table className="w-full text-left border-collapse min-w-[1000px]">
                            <thead className="bg-slate-50 dark:bg-dark-900 sticky top-0 z-10 border-b border-slate-100 dark:border-dark-600">
                                <tr>
                                    <th className="px-6 py-3 w-12">
                                        <input type="checkbox" className="rounded border-slate-300 text-primary focus:ring-primary/20" />
                                    </th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Rate name</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Tax agency</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Percentage</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Last updated</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Updated by</th>
                                    <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Part of group</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50 dark:divide-dark-600">
                                {MOCK_TAXES.map(tax => (
                                    <tr key={tax.id} className="hover:bg-slate-50/50 dark:hover:bg-dark-800/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <input type="checkbox" className="rounded border-slate-300 text-primary focus:ring-primary/20" />
                                        </td>
                                        <td className="px-6 py-4 text-xs font-bold text-primary hover:underline cursor-pointer pl-1">{tax.name}</td>
                                        <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-dark-200">{tax.agency}</td>
                                        <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-dark-200">{tax.percentage}</td>
                                        <td className="px-6 py-4 text-xs font-medium text-slate-500 dark:text-dark-400">{tax.lastUpdated}</td>
                                        <td className="px-6 py-4">
                                            <Badge variant="soft" color="success" className="rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-success-light/10 shadow-none ring-0">
                                                {tax.status}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-xs font-medium text-slate-600 dark:text-dark-200">{tax.updatedBy}</td>
                                        <td className="px-6 py-4 text-xs font-medium text-slate-400 dark:text-dark-500">{tax.group}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-3 bg-slate-50/50 dark:bg-dark-900/30 border-t border-slate-100 dark:border-dark-600 flex items-center justify-between mt-auto">
                    <span className="text-[11px] font-bold text-slate-400 dark:text-dark-500 tracking-wider uppercase pl-1">
                        1-1 of 1 items
                    </span>
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 rounded bg-white dark:bg-dark-800 border border-slate-200 dark:border-dark-600 text-slate-400 disabled:opacity-50" disabled>
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button className="p-1.5 rounded bg-white dark:bg-dark-800 border border-slate-200 dark:border-dark-600 text-slate-400 disabled:opacity-50" disabled>
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <AddTaxRateModal show={showAddTax} onClose={() => setShowAddTax(false)} />
      <TaxesFilterDrawer show={showFilter} onClose={() => setShowFilter(false)} />
    </>
  );
}
